import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog"
import { RadioGroup, RadioGroupItem, RadioGroupLabel } from "@/components/ui/radio-group"
import {
  BETTER_DOC_REJECTION_REASON_ITEMS,
  isBetterDocRejectionReason,
} from "@/modules/patients/detail/better-doc/constants"
import { BetterDocRejectionReason } from "@/types/__generated__/union-types"

type BetterDocRejectionReasonDialogProps = {
  open: boolean
  isConfirming: boolean
  onCancel: () => void
  onConfirm: (betterDocRejectionReason: BetterDocRejectionReason) => void
  onCloseAutoFocus: (event: Event) => void
}

type BetterDocRejectionReasonSelectionProps = Pick<
  BetterDocRejectionReasonDialogProps,
  "isConfirming" | "onCancel" | "onConfirm"
>

const BetterDocRejectionReasonSelection = ({
  isConfirming,
  onCancel,
  onConfirm,
}: BetterDocRejectionReasonSelectionProps): React.JSX.Element => {
  const { t } = useTranslation()
  const [selectedBetterDocRejectionReason, setSelectedBetterDocRejectionReason] =
    useState<BetterDocRejectionReason | null>(null)

  const handleValueChange = (value: string): void => {
    if (isBetterDocRejectionReason(value)) {
      setSelectedBetterDocRejectionReason(value)
    }
  }

  const handleConfirm = (): void => {
    if (selectedBetterDocRejectionReason === null) return

    onConfirm(selectedBetterDocRejectionReason)
  }

  return (
    <>
      <div className="flex flex-col gap-2 px-6 py-2">
        <RadioGroup value={selectedBetterDocRejectionReason ?? undefined} onValueChange={handleValueChange}>
          {BETTER_DOC_REJECTION_REASON_ITEMS.map((item) => (
            <RadioGroupItem
              key={item.value}
              value={item.value}
              id={item.value}
              data-testid={item.testId}
              className="w-auto">
              <RadioGroupLabel htmlFor={item.value}>{t(item.labelKey)}</RadioGroupLabel>
            </RadioGroupItem>
          ))}
        </RadioGroup>
      </div>
      <DialogFooter>
        <Button variant="secondary" onClick={onCancel} disabled={isConfirming} data-testid="dialog-cancel-button">
          {t("common.button.cancel")}
        </Button>
        <Button
          onClick={handleConfirm}
          isLoading={isConfirming}
          disabled={selectedBetterDocRejectionReason === null || isConfirming}
          data-testid="better-doc-rejection-reason-confirm-button">
          {t("common.button.confirm")}
        </Button>
      </DialogFooter>
    </>
  )
}

export const BetterDocRejectionReasonDialog = ({
  open,
  isConfirming,
  onCancel,
  onConfirm,
  onCloseAutoFocus,
}: BetterDocRejectionReasonDialogProps): React.JSX.Element => {
  const { t } = useTranslation()

  return (
    <Dialog open={open} onOpenChange={isConfirming ? undefined : onCancel}>
      <DialogPortal>
        <DialogOverlay onMouseDown={(e) => e.stopPropagation()} />
        <DialogContent
          variant="small"
          data-testid="better-doc-rejection-reason-dialog"
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onCloseAutoFocus={onCloseAutoFocus}>
          <DialogHeader>
            <DialogTitle>{t("episodeOfCare.betterDoc.confirmationTitle")}</DialogTitle>
            <DialogDescription>{t("episodeOfCare.betterDoc.confirmationDescription")}</DialogDescription>
          </DialogHeader>
          <BetterDocRejectionReasonSelection isConfirming={isConfirming} onCancel={onCancel} onConfirm={onConfirm} />
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}
